/**
 * 地図右上のツールバー（マップスタイル / 言語・要件19-1 / 20-3）。
 *
 * MapLibre のコントロール枠（PortalControl が用意した箱）へ createPortal で描かれる。
 * 位置・余白・角丸・ボタン間の区切り線は MapLibre の CSS が面倒を見るので、
 * 旧 LangControl / MapStyleControl のような絶対座標のマジックナンバーは持たない。
 *
 * アイコン1つにつきポップオーバー1つ。開くのは同時に1つだけ（open の単一 state で排他）。
 * 閉じるのは「再クリック」「選択」「外側 pointerdown」「ESC」の4通り。
 *
 * 3つ目の「?」はポップオーバーではなくヘルプのモーダルを開く（タスク50・HelpModal は body に portal で描く）。
 * 4つ目の 📡 はライブアクティビティフィードの ON/OFF スイッチ（タスク54・ポップオーバーなし）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LANG_IDS, type Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { MAP_STYLE_IDS, type MapStyleId } from './mapStyle';
import { HelpModal } from './HelpModal';
import './mapToolbar.css';

/** 開いているポップオーバー。null は全て閉じている。 */
type OpenPopover = 'style' | 'lang' | null;

// ESC で閉じたときのフォーカス戻しと aria-controls 用の固定 id。
// ツールバーは画面に1つしか無いので固定値で衝突しない。
const STYLE_BTN_ID = 'map-toolbar-style-btn';
const LANG_BTN_ID = 'map-toolbar-lang-btn';
const STYLE_POPOVER_ID = 'map-toolbar-style-popover';
const LANG_POPOVER_ID = 'map-toolbar-lang-popover';
const HELP_BTN_ID = 'map-toolbar-help-btn';
const FEED_BTN_ID = 'map-toolbar-feed-btn';

/**
 * マップスタイル（重ねた地図シート）のアイコン。
 * 色はボタンの color を継承する（currentColor）。
 */
function LayersIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3 3 7.5 12 12l9-4.5z" />
      <path d="m3 12.5 9 4.5 9-4.5" />
      <path d="m3 17 9 4.5 9-4.5" />
    </svg>
  );
}

/** 言語（地球）のアイコン。LayersIcon と viewBox・stroke 幅を揃える。 */
function GlobeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.4 2.5 3.8 5.7 3.8 9S14.4 18.5 12 21c-2.4-2.5-3.8-5.7-3.8-9S9.6 5.5 12 3z" />
    </svg>
  );
}

/** ヘルプ（丸の中の ?）のアイコン。LayersIcon と viewBox・stroke 幅を揃える。 */
function HelpIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.2a2.6 2.6 0 0 1 5 .8c0 1.7-2.5 2.3-2.5 3.8" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** アクティビティフィード（電波を発する点）のアイコン。LayersIcon と viewBox・stroke 幅を揃える。 */
function FeedIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="1.8" />
      <path d="M8.2 15.8a5.4 5.4 0 0 1 0-7.6" />
      <path d="M15.8 8.2a5.4 5.4 0 0 1 0 7.6" />
      <path d="M5.3 18.7a9.5 9.5 0 0 1 0-13.4" />
      <path d="M18.7 5.3a9.5 9.5 0 0 1 0 13.4" />
    </svg>
  );
}

export interface MapToolbarProps {
  readonly lang: Lang;
  readonly mapStyle: MapStyleId;
  readonly onLangChange: (lang: Lang) => void;
  readonly onMapStyleChange: (style: MapStyleId) => void;
  /** ライブアクティビティフィードの ON/OFF（タスク54）。 */
  readonly feedEnabled: boolean;
  readonly onFeedToggle: () => void;
}

export function MapToolbar({
  lang,
  mapStyle,
  onLangChange,
  onMapStyleChange,
  feedEnabled,
  onFeedToggle,
}: MapToolbarProps) {
  const [open, setOpen] = useState<OpenPopover>(null);
  /** ヘルプのモーダル（ポップオーバーの排他とは別・タスク50）。 */
  const [helpOpen, setHelpOpen] = useState(false);
  const closeHelp = useCallback(() => {
    setHelpOpen(false);
    // 閉じたら「?」ボタンへフォーカスを戻す（キーボード操作で迷子にならないように）
    document.getElementById(HELP_BTN_ID)?.focus();
  }, []);
  // 外側クリック判定の基準（ボタン列とポップオーバーの両方を含む）。
  const rootRef = useRef<HTMLDivElement | null>(null);
  const m = messages(lang);

  /** スタイル名 → 表示ラベル（Hybrid は利用者から見れば「衛星」）。 */
  const styleLabels: Record<MapStyleId, string> = {
    Standard: m.mapStyle.standard,
    Monochrome: m.mapStyle.monochrome,
    Hybrid: m.mapStyle.satellite,
  };
  const langLabels: Record<Lang, string> = { ja: m.lang.ja, en: m.lang.en };

  /** 同じアイコンなら閉じ、違うアイコンなら乗り換える（同時に開くのは1つ）。 */
  const toggle = useCallback((which: 'style' | 'lang') => {
    setOpen((prev) => (prev === which ? null : which));
  }, []);

  // 外側クリックと ESC で閉じる。開いている間だけ listener を張る。
  // mousedown ではなく pointerdown にしているのは、地図キャンバスがタッチ操作で
  // touchstart を preventDefault するため、端末によってはエミュレートされた
  // mousedown が来ず「地図をタップしても閉じない」が起きるため。
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setOpen(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      // 閉じたらトグルボタンへフォーカスを戻す（キーボード操作で迷子にならないように）
      const triggerId = open === 'style' ? STYLE_BTN_ID : LANG_BTN_ID;
      setOpen(null);
      document.getElementById(triggerId)?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="map-toolbar" ref={rootRef}>
      {/* アイコン列。maplibregl-ctrl-group はここに付ける（箱側に付けると
          `.maplibregl-ctrl-group button` が子孫セレクタで効き、ポップオーバー内の
          選択肢ボタンまで 29px 角に潰れる）。 */}
      <div
        className="maplibregl-ctrl-group map-toolbar__group"
        role="group"
        aria-label={m.toolbar.label}
      >
        <button
          type="button"
          id={STYLE_BTN_ID}
          className={`map-toolbar__btn${open === 'style' ? ' map-toolbar__btn--open' : ''}`}
          onClick={() => toggle('style')}
          title={`${m.mapStyle.toggleTitle}（${styleLabels[mapStyle]}）`}
          aria-label={m.mapStyle.toggleTitle}
          aria-haspopup="true"
          aria-expanded={open === 'style'}
          aria-controls={STYLE_POPOVER_ID}
        >
          <LayersIcon />
        </button>
        <button
          type="button"
          id={LANG_BTN_ID}
          className={`map-toolbar__btn${open === 'lang' ? ' map-toolbar__btn--open' : ''}`}
          onClick={() => toggle('lang')}
          title={`${m.lang.toggleTitle}（${langLabels[lang]}）`}
          aria-label={m.lang.toggleTitle}
          aria-haspopup="true"
          aria-expanded={open === 'lang'}
          aria-controls={LANG_POPOVER_ID}
        >
          <GlobeIcon />
        </button>
        <button
          type="button"
          id={HELP_BTN_ID}
          className="map-toolbar__btn"
          onClick={() => {
            setOpen(null);
            setHelpOpen(true);
          }}
          title={m.help.button}
          aria-label={m.help.button}
          aria-haspopup="dialog"
        >
          <HelpIcon />
        </button>
        <button
          type="button"
          id={FEED_BTN_ID}
          role="switch"
          aria-checked={feedEnabled}
          className={`map-toolbar__btn${feedEnabled ? ' map-toolbar__btn--on' : ''}`}
          onClick={() => {
            setOpen(null);
            onFeedToggle();
          }}
          title={`${m.feed.label}（${feedEnabled ? m.feed.on : m.feed.off}）`}
          aria-label={m.feed.label}
        >
          <FeedIcon />
        </button>
      </div>

      {helpOpen && <HelpModal lang={lang} onClose={closeHelp} />}

      {open === 'style' && (
        <div
          className="map-toolbar__popover"
          id={STYLE_POPOVER_ID}
          role="radiogroup"
          aria-label={m.mapStyle.toggleTitle}
        >
          {MAP_STYLE_IDS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={mapStyle === id}
              className={`map-toolbar__option${
                mapStyle === id ? ' map-toolbar__option--active' : ''
              }`}
              onClick={() => {
                onMapStyleChange(id);
                setOpen(null);
              }}
            >
              {styleLabels[id]}
            </button>
          ))}
        </div>
      )}

      {open === 'lang' && (
        <div
          className="map-toolbar__popover"
          id={LANG_POPOVER_ID}
          role="radiogroup"
          aria-label={m.lang.toggleTitle}
        >
          {LANG_IDS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={lang === id}
              className={`map-toolbar__option${
                lang === id ? ' map-toolbar__option--active' : ''
              }`}
              onClick={() => {
                onLangChange(id);
                setOpen(null);
              }}
            >
              {langLabels[id]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
